import cn from 'classnames';
import {ButtonHTMLAttributes, FC, KeyboardEvent, ReactNode} from 'react';
import styles from './RegistrationButton.module.css';

export type RegistrationButtonVariant = 'primary' | 'secondary' | 'muted';
export type RegistrationButtonSize = 'fill' | 'auto';

type Props = {
	children: ReactNode;
	variant: RegistrationButtonVariant;
	size?: RegistrationButtonSize;
	pressed?: boolean;
	loading?: boolean;
	className?: string;
	onClick?: () => void;
} & Pick<ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'disabled' | 'aria-pressed'>;

export const RegistrationButton: FC<Props> = ({
	children,
	variant,
	size = 'fill',
	pressed = false,
	loading = false,
	className,
	onClick,
	disabled,
	'aria-pressed': ariaPressed,
}) => {
	const isDisabled = disabled || loading;

	const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		if (isDisabled) {
			return;
		}

		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			onClick?.();
		}
	};

	return (
		<div
			role="button"
			tabIndex={isDisabled ? -1 : 0}
			aria-disabled={isDisabled}
			aria-busy={loading || undefined}
			aria-pressed={ariaPressed ?? (variant === 'muted' ? pressed : undefined)}
			className={cn(
				styles.root,
				styles[variant],
				size === 'fill' ? styles.fill : styles.auto,
				variant === 'muted' && pressed && styles.pressed,
				loading && styles.loading,
				className,
			)}
			onClick={isDisabled ? undefined : onClick}
			onKeyDown={handleKeyDown}
		>
			{loading ? <span className={cn(styles.spinner, styles[`spinner_${variant}`])} aria-hidden /> : null}
			<span className={cn(styles.label, loading && styles.labelHidden)}>{children}</span>
		</div>
	);
};
